import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { getRoutePlansAndBillings } from 'modules/v2/routes/navigation';
import { useSubscriptionCancellation } from './useSubscriptionCancellation';
import {
  clearReactivation,
  dismissPlanChange,
  isPlanChangeDismissed,
  Reactivation,
  REACTIVATION_EVENT,
  readReactivation,
  selectSubscriptionNotice,
  SubscriptionNotice,
} from '../subscriptionNotice';

type SubscriptionNoticeReturn = {
  notice: SubscriptionNotice | null;
  dismiss: () => void;
};

/**
 * The subscription notice to show under the nav bar (cancellation, kept
 * membership, scheduled plan change or completed plan change). The scheduled
 * plan change shows on Plans & billing only. The kept membership and the
 * completed plan change can be dismissed; the first lasts for the browser
 * session, the second's dismissal is remembered per ramp in localStorage.
 */
export const useSubscriptionNotice = (): SubscriptionNoticeReturn => {
  const {
    isScheduledForCancellation,
    scheduledCancelDate,
    scheduledPlanChange,
    lastPlanChange,
  } = useSubscriptionCancellation();
  const [dismissedRampId, setDismissedRampId] = useState<string | null>(null);
  const { pathname } = useLocation();
  const [reactivation, setReactivation] = useState<Reactivation | null>(readReactivation);

  useEffect(() => {
    const onReactivation = (event: Event) =>
      setReactivation((event as CustomEvent<Reactivation | null>).detail);
    window.addEventListener(REACTIVATION_EVENT, onReactivation);
    return () => window.removeEventListener(REACTIVATION_EVENT, onReactivation);
  }, []);

  const notice = selectSubscriptionNotice({
    isScheduledForCancellation,
    scheduledCancelDate,
    scheduledPlanChange,
    lastPlanChange,
    isDismissed: (rampId) => rampId === dismissedRampId || isPlanChangeDismissed(rampId),
    isOnPlansAndBilling: pathname === getRoutePlansAndBillings(),
    reactivation,
  });

  const dismiss = useCallback(() => {
    if (notice?.kind === 'reactivated') {
      clearReactivation();
      return;
    }
    if (notice?.kind !== 'planChanged') return;
    dismissPlanChange(notice.change.rampId);
    setDismissedRampId(notice.change.rampId);
  }, [notice]);

  return { notice, dismiss };
};
